import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { PatientSearchSelect } from "@/components/PatientPicker";
import { RescheduleDialog } from "@/components/RescheduleDialog";
import { CancelApptDialog } from "@/components/CancelApptDialog";
import { CalendarClock, Loader2, ArrowRight, XCircle, History } from "lucide-react";

function when(iso) {
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function ChangeRow({ c }) {
  return (
    <li className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2 text-sm last:border-0" data-testid={`change-${c.id}`}>
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.action === "cancel" ? "bg-red-100 text-red-700" : "bg-teal-100 text-teal-700"}`}>{c.action}</span>
      <span className="text-slate-500">{c.from_label}</span>
      {c.to_label && (<><ArrowRight className="h-3 w-3 text-slate-400" /><span className="font-medium text-slate-800">{c.to_label}</span></>)}
      {c.note && <span className="text-xs italic text-slate-400">“{c.note}”</span>}
      <span className="ml-auto text-[11px] text-slate-400">{c.actor} · {when(c.ts)}</span>
    </li>
  );
}

export default function Appointments() {
  const [pid, setPid] = useState("");
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changes, setChanges] = useState([]);
  const [resched, setResched] = useState(null);
  const [cancel, setCancel] = useState(null);

  const loadChanges = useCallback(() => api.get("/admin/appointment-changes").then((r) => setChanges(r.data || [])).catch(() => {}), []);
  const loadAppts = useCallback(() => {
    if (!pid) return;
    setLoading(true);
    api.get(`/admin/patients/${pid}/appointments`).then((r) => setAppts(r.data.appointments || []))
      .catch(() => setAppts([])).finally(() => setLoading(false));
  }, [pid]);
  useEffect(() => { loadChanges(); }, [loadChanges]);
  useEffect(() => { setAppts([]); loadAppts(); }, [loadAppts]);

  const done = () => { setResched(null); setCancel(null); loadAppts(); loadChanges(); };

  return (
    <Layout title="Appointments" subtitle="Reschedule or cancel a patient's upcoming Cliniko appointments">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-3">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><CalendarClock className="h-4 w-4 text-teal-600" /> Upcoming appointments</div>
          <PatientSearchSelect value={pid} onChange={setPid} placeholder="Search patient..." testid="appts-patient-select" />
          {loading && <Loader2 className="h-5 w-5 animate-spin text-teal-600" />}
          {!loading && pid && appts.length === 0 && <p className="text-sm text-slate-500" data-testid="appts-empty">No upcoming appointments for this patient.</p>}
          <ul className="space-y-2" data-testid="appts-list">
            {appts.map((a) => (
              <li key={a.appt_id} className="flex flex-col gap-2 rounded-xl border border-slate-200 p-3 sm:flex-row sm:items-center" data-testid={`appt-${a.appt_id}`}>
                <span className="flex-1 text-sm font-medium text-slate-800">{a.label}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setResched(a)} data-testid={`appt-reschedule-${a.appt_id}`}>Reschedule</Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setCancel(a)} data-testid={`appt-cancel-${a.appt_id}`}><XCircle className="mr-1 h-3.5 w-3.5" /> Cancel</Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800"><History className="h-4 w-4 text-teal-600" /> Recent changes</div>
          {changes.length === 0 ? <p className="text-sm text-slate-500" data-testid="changes-empty">No changes yet.</p> : (
            <ul data-testid="changes-list">{changes.slice(0, 30).map((c) => <ChangeRow key={c.id} c={c} />)}</ul>
          )}
        </div>
      </div>
      <RescheduleDialog appt={resched} patientId={pid} onClose={() => setResched(null)} onDone={done} />
      <CancelApptDialog appt={cancel} patientId={pid} onClose={() => setCancel(null)} onDone={done} />
    </Layout>
  );
}
